/**
 * <cart-upsell>: the "Pairs well with ..." rows in the cart drawer (ATB redesign 2026-10).
 *
 * - Seeds Shopify's related recommendations (/recommendations/products.json, intent=related) on the item the
 *   customer added last; skips products already in the cart and sold-out products; shows up to 3.
 * - Short titles come from the custom.short_title metafield via sections/upsell-data.liquid.
 * - No-jump rule: rows are drawn when the drawer is closed or re-rendered. While the drawer is open a cart change
 *   only updates button states (Add, Adding, Added, Retry), which share one fixed-size button.
 * - Quick Add for single-variant products. Products with options get "Choose", a link to the product page.
 * - The add goes through <cart-drawer>.renderContents(), which patches the open drawer in place.
 */
class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.inner = this.querySelector('.cd-recs__inner');
    this.list = this.querySelector('.cd-recs__list');
    this.label = this.querySelector('[data-upsell-label]');
    this.template = this.querySelector('.cart-upsell__card-template');
    this.maxCards = parseInt(this.dataset.maxCards, 10) || 3;
    this.candidateLimit = parseInt(this.dataset.candidateLimit, 10) || 10;
    this.routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this._refreshing = false;
  }

  connectedCallback() {
    this.refresh();
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCart = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    }
  }

  disconnectedCallback() {
    if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
  }

  drawerOpen() {
    const drawer = this.closest('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  }

  onCartUpdate(event) {
    if (this.drawerOpen()) {
      this.syncStates(event && event.cartData);
      return;
    }
    this.refresh();
  }

  async refresh() {
    if (this._refreshing || !this.inner) return;
    this._refreshing = true;
    try {
      const cart = await fetch(`${this.routeRoot}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
      if (!cart.items || cart.items.length === 0) {
        this.hide();
        return;
      }
      const inCart = new Set(cart.items.map((item) => item.product_id));
      // Shopify lists the most recently added line first.
      const seed = cart.items[0];
      const recsUrl = `${this.routeRoot}recommendations/products.json?product_id=${seed.product_id}&limit=${this.candidateLimit}&intent=related`;
      const sectionUrl = `${this.routeRoot}recommendations/products?product_id=${seed.product_id}&limit=${this.candidateLimit}&intent=related&section_id=upsell-data`;
      const [res, sectionRes] = await Promise.all([
        fetch(recsUrl, { credentials: 'same-origin' }),
        fetch(sectionUrl, { credentials: 'same-origin' }).catch(() => null),
      ]);
      if (!res.ok) {
        this.hide();
        return;
      }
      const data = await res.json();
      const shortTitles = {};
      if (sectionRes && sectionRes.ok) {
        try {
          const html = await sectionRes.text();
          const match = html.match(/<script[^>]*data-upsell-meta[^>]*>([\s\S]*?)<\/script>/);
          if (match) {
            JSON.parse(match[1]).forEach(({ handle, short_title }) => {
              if (handle && short_title) shortTitles[handle] = short_title;
            });
          }
        } catch (e) {
          /* fall back to product.title */
        }
      }
      const products = (data.products || [])
        .filter((p) => !inCart.has(p.id))
        .filter((p) => (p.variants || []).some((v) => v.available))
        .slice(0, this.maxCards);
      if (!products.length) {
        this.hide();
        return;
      }
      if (this.drawerOpen() && !this.inner.hidden) return; // never redraw under the customer's thumb
      this.label.textContent = `Pairs well with ${seed.product_title}`;
      this.render(products, shortTitles);
      this.show();
    } catch (err) {
      console.warn('[cart-upsell] refresh failed:', err);
      this.hide();
    } finally {
      this._refreshing = false;
    }
  }

  show() {
    this.inner.hidden = false;
  }

  hide() {
    this.inner.hidden = true;
  }

  render(products, shortTitles) {
    this.list.innerHTML = '';
    products.forEach((product) => {
      const row = this.template.content.firstElementChild.cloneNode(true);
      const url = product.url || `${this.routeRoot}products/${product.handle}`;
      const title = shortTitles[product.handle] || product.title;
      row.dataset.productId = product.id;

      const media = row.querySelector('.cd-thumb');
      media.href = url;
      const img = row.querySelector('img');
      const src = this._imageSrc(product);
      if (src) img.src = this._sizedImage(src, '224x224');
      else img.remove();

      const name = row.querySelector('.cd-rec__name');
      name.href = url;
      name.textContent = title;
      row.querySelector('.cd-rec__amt').textContent = this._formatMoney(product.price);

      const button = row.querySelector('button.cd-add');
      const choose = row.querySelector('.cd-add--link');
      const variants = product.variants || [];
      const available = variants.filter((v) => v.available);
      if (variants.length === 1 && available.length === 1) {
        button.dataset.variantId = available[0].id;
        button.dataset.title = title;
        button.setAttribute('aria-label', `Add ${title}`);
        button.addEventListener('click', () => this.quickAdd(available[0].id, row, button));
        choose.remove();
      } else {
        button.remove();
        choose.hidden = false;
        choose.href = url;
        choose.setAttribute('aria-label', `Choose options for ${title}`);
      }
      this.list.appendChild(row);
    });
  }

  async quickAdd(variantId, row, button) {
    if (button.classList.contains('is-adding') || button.getAttribute('aria-disabled') === 'true') return;
    const fail = row.querySelector('.cd-rec__fail');
    row.classList.remove('is-failed');
    button.classList.remove('is-failed');
    fail.textContent = '';
    button.classList.add('is-adding');
    const drawer = document.querySelector('cart-drawer');
    const sections =
      drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((s) => s.id)
        : ['cart-drawer', 'cart-icon-bubble'];
    try {
      const res = await fetch(`${this.routeRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'add failed');
      button.classList.remove('is-adding');
      button.classList.add('is-added');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', `${button.dataset.title || 'Item'} added to your cart`);
      if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(data);
      if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-upsell',
          productVariantId: String(variantId),
          cartData: data,
        });
      }
    } catch (err) {
      console.warn('[cart-upsell] quick add failed:', err);
      button.classList.remove('is-adding');
      button.classList.add('is-failed');
      row.classList.add('is-failed');
      fail.textContent = "Couldn't add it.";
      button.setAttribute('aria-label', `Try adding ${button.dataset.title || 'this item'} again`);
    }
  }

  /* While the drawer is open: only button states change. An "Added" product that left the cart goes back to "Add". */
  async syncStates(cartData) {
    try {
      let items = cartData && Array.isArray(cartData.items) && cartData.item_count !== undefined ? cartData.items : null;
      if (!items) {
        const cart = await fetch(`${this.routeRoot}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
        items = cart.items || [];
      }
      const inCart = new Set(items.map((item) => String(item.product_id)));
      this.querySelectorAll('.cd-rec').forEach((row) => {
        const button = row.querySelector('button.cd-add');
        if (!button || !button.classList.contains('is-added')) return;
        if (!inCart.has(String(row.dataset.productId))) {
          button.classList.remove('is-added');
          button.removeAttribute('aria-disabled');
          button.setAttribute('aria-label', `Add ${button.dataset.title || 'item'}`);
        }
      });
    } catch (e) {
      /* states stay as they are */
    }
  }

  _imageSrc(product) {
    if (!product) return null;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && (product.featured_image.url || product.featured_image.src)) {
      return product.featured_image.url || product.featured_image.src;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first.url;
    }
    return null;
  }

  _sizedImage(src, size) {
    if (!src) return '';
    let url = src.startsWith('//') ? `https:${src}` : src;
    url = url.replace(/(\.(?:jpe?g|png|gif|webp|avif))(\?|$)/i, `_${size}$1$2`);
    return url;
  }

  _formatMoney(price) {
    const cents = typeof price === 'string' ? parseInt(price, 10) : price;
    if (!Number.isFinite(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
      } catch (_) {
        /* fall through */
      }
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
